import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PackageCheck, Star } from "lucide-react";
import { Column, DataTable } from "@/components/ui/data-table";
import { MetricCard, MetricCardsGrid } from "../../shared";
import { RevenueChartCard } from "../../revenue/RevenueChartCard";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../../services/constants";
import { translateOffersLabel } from "../translateOffersLabel";
import type { OfferSection, OfferTableRow } from "../types";

interface OffersSectionWidgetProps {
  section: OfferSection;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const OffersSectionWidget: React.FC<OffersSectionWidgetProps> = ({
  section,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();

  const columns: Column<OfferTableRow>[] = useMemo(
    () => [
      {
        key: "serviceName",
        header: t("analytics.offers.table.offer", "Offer"),
        className: "min-w-[200px] max-w-[320px] text-left align-middle",
        render: (_value, row) => (
          <div className="flex items-center gap-2">
            {row.imageUrl ? (
              <img
                src={row.imageUrl}
                alt={row.serviceName}
                className="size-9 shrink-0 rounded-md object-cover"
                loading="lazy"
              />
            ) : (
              <div
                className="size-9 shrink-0 rounded-md bg-[#F4F4F5]"
                aria-hidden
              />
            )}
            <div className="min-w-0">
              <p
                className={
                  row.isDeleted
                    ? "truncate text-sm font-medium text-[#A3A3A3] line-through"
                    : "truncate text-sm font-medium text-[#171717]"
                }
              >
                {row.serviceName}
              </p>
              {row.isDeleted ? (
                <p className="text-xs text-[#737373]">
                  {t("analytics.offers.table.deleted", "Deleted")}
                </p>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "catalogPrice",
        header: t("analytics.offers.table.catalogPrice", "Catalog price"),
        className: "text-center text-[#171717]",
      },
      {
        key: "opened",
        header: t("analytics.offers.table.opened", "Opened"),
        className: "text-center text-[#171717]",
      },
      {
        key: "ordered",
        header: t("analytics.offers.table.ordered", "Ordered"),
        className: "text-center text-[#171717]",
      },
      {
        key: "revenue",
        header: t("analytics.offers.table.revenue", "Revenue"),
        className: "text-center text-[#171717]",
      },
    ],
    [t],
  );

  return (
    <RevenueChartCard
      title={translateOffersLabel(t, section.title)}
      icon={<Star />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      showExport={Boolean(onExport)}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <MetricCardsGrid columns={4}>
        {section.metrics.map((metric) => (
          <MetricCard
            key={`${section.id}-${metric.title}`}
            title={translateOffersLabel(t, metric.title)}
            value={metric.value}
            width="fill"
            icon={<PackageCheck />}
          />
        ))}
      </MetricCardsGrid>

      <DataTable<OfferTableRow>
        data={section.rows}
        columns={columns}
        getRowKey={(row) => row.id}
        withPagination={false}
        withSorting={false}
        className="space-y-0"
        columnHeaderClassName="text-[10px] font-medium text-[#737373]"
        emptyMessage={t(
          "analytics.offers.table.empty",
          "No offers in this section",
        )}
      />
    </RevenueChartCard>
  );
};
